import { Component, ElementRef, OnInit } from '@angular/core';
import { Subject, takeUntil, tap } from "rxjs";
import { CompressImageService } from "src/app/_services/compress-image.service";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { MessageService } from "primeng/api";
import { TranslateService } from "@ngx-translate/core";
import { SpecialOfferModel } from "src/app/admin-panel/models/business/special-offer.model";
import { SpecialOfferService } from "src/app/admin-panel/services/business/special-offer.service";
import { DatePipe } from "@angular/common";
import { SpecialProductProject } from "src/app/admin-panel/models/business/special-product-project.model";

@Component({
  selector: 'app-add-or-edit-special-offer',
  templateUrl: './add-or-edit-special-offer.component.html',
  styleUrls: ['./add-or-edit-special-offer.component.scss']
})
export class AddOrEditSpecialOfferComponent implements OnInit {

  //#region Fields
  public unsubscribe: Subject<void> = new Subject<void>();
  public spinner: boolean = false
  public enableEdit: boolean = false;
  public specialOfferModel: SpecialOfferModel;
  public errorModel: { [k: string]: any } = {};
  public countries: Array<number> = new Array<number>();
  public projectList: Array<{name: string, value: number}> = new Array<{name: string, value: number}>();
  public projectsSelected: Array<number> = new Array<number>();
  public freeProjectsSelected: Array<number> = new Array<number>();
  public requiredFieldErrorMessage: string = "BUSINESS.REQUIRED_FIELD";
  public invalidInputErrorMessage: string = "INFO.INVALID_INPUT";
  //#endregion

  //#region Constructor
  constructor(
    private openFile: ElementRef,
    private compressImage: CompressImageService,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private service: SpecialOfferService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {
    this.specialOfferModel = new SpecialOfferModel();
    if (this.config.data != undefined && this.config.data.projects) {
      this.projectList = this.config.data.projects;
      this.getSpecialOffer();
    }
  }

  ngOnInit(): void {
    this.getAllCountries();
  }
  //#endregion

  //#region Public Methods
  public addOrEditSpecialOffer(model: SpecialOfferModel): void {
    this.errorModel = this.generateErrorMessages(model);

    if (Object.keys(this.errorModel).length !== 0) {
      return;
    }

    const projects = this.projectsSelected.map(id => new SpecialProductProject(id, true));
    const freeProjects = this.freeProjectsSelected.map(id => new SpecialProductProject(id, false));

    model.specialProductProjectsModel = [...projects, ...freeProjects];
    model.validUtilDate = new DatePipe('en-US').transform(model.validUtilDate, 'yyyy-MM-ddTHH:mm:ss')!;

    this.spinner = true;

    const apiCall = this.enableEdit ? this.service.updateSpecialProduct(model) : this.service.addSpecialProduct(model);

    apiCall.pipe(
      takeUntil(this.unsubscribe),
      tap({
        next: () => this.handleSuccess(),
        error: errorResponse => this.handleError(errorResponse)
      })
    ).subscribe();
  }

  public removeSpecialOffer(specialOfferModel: SpecialOfferModel): void {
    this.spinner = true;
    this.service.deleteSpecialProduct(specialOfferModel.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: _ => {
          this.spinner = false;
          this.ref.close({removed: true});
        },
        error: _ => {
          this.spinner = false;
        }
      });
  }

  public getSpecialOffer(): void {
    this.spinner = true;
    this.service.getSpecialProduct()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          if (response) {
            this.specialOfferModel = response;
            this.projectsSelected = response.specialProductProjects.filter((x: SpecialProductProject) => !x.isPrimaryProject).map((x: SpecialProductProject) => x.projectId);
            this.freeProjectsSelected = response.specialProductProjects.filter((x: SpecialProductProject) => x.isPrimaryProject).map((x: SpecialProductProject) => x.projectId);
            this.specialOfferModel.validUtilDate = new Date(this.specialOfferModel.validUtilDate);
            this.enableEdit = true;
          }
          this.spinner = false;
        },
        error: _ => {
          this.spinner = false;
        }
      });
  }

  public getAllCountries(): void {
    this.service.getAllCountryNames()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.countries = response;
        },
        error: errorResponse => {
          if (errorResponse.error) {
            this.messageService.add({ severity: 'error', summary: "Server Error", life: 2000 });
          }
        }
      });
  }

  public cancel(): void {
    this.ref.close(false);
  }
  //#endregion

  //#Private Methods
  private generateErrorMessages(model: SpecialOfferModel) {
    let errorModel: { [k: string]: any } = {};

    if (!model.validUtilDate) {
      errorModel['Date'] = this.requiredFieldErrorMessage;
    }

    if (!model.name || model.name.trim() === "") {
      errorModel['Name'] = this.requiredFieldErrorMessage;
    }

    if (!model.description || model.description.trim() === "") {
      errorModel['Description'] = this.requiredFieldErrorMessage;
    }

    if (!model.countriesId || model.countriesId.length === 0) {
      errorModel['countryIds'] = this.requiredFieldErrorMessage;
    }

    if (this.projectsSelected.length === 0) {
      errorModel['apps'] = this.requiredFieldErrorMessage;
    }

    if (this.freeProjectsSelected.length === 0) {
      errorModel['appsFree'] = this.requiredFieldErrorMessage;
    }

    if (model.yearlyPrice === null || model.yearlyPrice === undefined) {
      errorModel['YPrice'] = this.requiredFieldErrorMessage;
    } else if (model.yearlyPrice <= 0) {
      errorModel['YPrice'] = this.invalidInputErrorMessage;
    }

    if (model.mounthlyPrice === null || model.mounthlyPrice === undefined) {
      errorModel['MPrice'] = this.requiredFieldErrorMessage;
    } else if (model.mounthlyPrice <= 0) {
      errorModel['MPrice'] = this.invalidInputErrorMessage;
    }

    if (model.maxNumberOfVenuesForPrimaryProjects === null || model.maxNumberOfVenuesForPrimaryProjects === undefined) {
      errorModel['UpTo'] = this.requiredFieldErrorMessage;
    } else if (model.maxNumberOfVenuesForPrimaryProjects <= 0) {
      errorModel['UpTo'] = this.invalidInputErrorMessage;
    }

    if (model.maxNumberOfVenuesForSecondaryProjects === null || model.maxNumberOfVenuesForSecondaryProjects === undefined) {
      errorModel['UpToFree'] = this.requiredFieldErrorMessage;
    } else if (model.maxNumberOfVenuesForSecondaryProjects <= 0) {
      errorModel['UpToFree'] = this.invalidInputErrorMessage;
    }

    return errorModel;
  }

  private handleSuccess(): void {
    this.spinner = false;
    this.ref.close(true);
  }

  private handleError(errorResponse: any): void {
    this.spinner = false;
    if (errorResponse.error) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('SYSTEM_PROFILES.VALIDATION_FAILED_MESSAGE'),
        life: 2000
      });
    }
  }
  //#endregion
}
